import type { DelegationSessionResponse } from '@1po/1po-bff-fe-spec/generated/delegation/response/DelegationSessionResponse';

const DELEGATION_RESTORE_MARKER_KEY = '1po.delegation.restore';

// Marker stores the backend-authoritative session expiry (epoch ms).
// This avoids the mismatch between a local write-time TTL and the real backend expiry.
type DelegationRestoreMarker = {
  expiresAt: number; // epoch ms from backend session.expiresAt
};

const parseRestoreMarker = (rawMarker: string | null): DelegationRestoreMarker | null => {
  if (!rawMarker) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawMarker) as DelegationRestoreMarker;
    if (typeof parsed?.expiresAt !== 'number' || parsed.expiresAt <= 0) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const hasDelegationRestoreMarker = (): boolean => {
  try {
    const rawMarker = globalThis.localStorage?.getItem(DELEGATION_RESTORE_MARKER_KEY) ?? null;

    // Backward compatibility: clear legacy plain-string marker; expiry is unknown so cannot restore.
    if (rawMarker === 'eligible') {
      globalThis.localStorage?.removeItem(DELEGATION_RESTORE_MARKER_KEY);
      return false;
    }

    const marker = parseRestoreMarker(rawMarker);
    if (!marker) {
      // Cleanup stale or malformed values eagerly.
      globalThis.localStorage?.removeItem(DELEGATION_RESTORE_MARKER_KEY);
      return false;
    }

    if (Date.now() >= marker.expiresAt) {
      globalThis.localStorage?.removeItem(DELEGATION_RESTORE_MARKER_KEY);
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

// Accepts the ISO date string from the backend session response.
// No-ops if expiresAt is absent or unparseable so in-memory session.active still allows same-tab restore.
export const markDelegationRestoreEligible = (expiresAt: string | undefined): void => {
  if (!expiresAt) {
    return;
  }

  try {
    const epochMs = new Date(expiresAt).getTime();
    if (!Number.isFinite(epochMs) || epochMs <= 0) {
      return;
    }

    const marker: DelegationRestoreMarker = { expiresAt: epochMs };
    globalThis.localStorage?.setItem(DELEGATION_RESTORE_MARKER_KEY, JSON.stringify(marker));
  } catch {
    // Ignore storage access issues; active in-memory session still allows restore.
  }
};

export const clearDelegationRestoreMarker = (): void => {
  try {
    globalThis.localStorage?.removeItem(DELEGATION_RESTORE_MARKER_KEY);
  } catch {
    // Ignore storage access issues.
  }
};

export const shouldRestoreDelegationSession = (session: DelegationSessionResponse | null | undefined): boolean =>
  Boolean(session?.active) || hasDelegationRestoreMarker();
